import {
  init,
  track,
  type PlausibleEventOptions,
} from "@plausible-analytics/tracker";

import { useUserStore } from "components/stores/UserStore";

// Initialize Plausible with configuration
// Note: domain is required by the new API - using window.location.hostname as default
init({
  domain: window.location.hostname,
  // leverage nginx proxy on production to avoid blocking requests by adblockers
  // endpoint:
  //   window.location.origin === "https://insight.plugport.no"
  //     ? "https://insight.plugport.no/api/event"
  //     : "https://plausible.io/api/event",
  captureOnLocalhost: true,
  autoCapturePageviews: false, // We manually track pageviews
  outboundLinks: false, // Can be enabled if needed
});

export const getDefaultPlausibleProps = () => {
  const user = useUserStore.getState().user;

  return {
    organisation: user?.UserV2?.organization?.Name ?? "",
    userRole: user?.UserV2?.user?.role ?? "",
    orgType: user?.UserV2?.organization?.Type ?? "",
  };
};

/**
 * Track a custom event with default props merged in
 */
const trackEvent = (
  eventName: string,
  options?: PlausibleEventOptions,
): void => {
  track(eventName, {
    ...options,
    props: {
      ...getDefaultPlausibleProps(),
      ...options?.props,
    },
  });
};

export const trackFunnelStep = (
  funnel: string,
  step: string,
  options?: PlausibleEventOptions,
): void => {
  trackEvent(`Funnel: ${funnel}`, {
    ...options,
    props: {
      step,
      ...getDefaultPlausibleProps(),
      ...options?.props,
    },
  });
};

/**
 * Track a pageview event
 * @param eventData - Optional data containing url and props
 * @param options - Additional options (props will be merged with eventData.props)
 */
const trackPageview = (
  eventData?: { url?: string; props?: Record<string, string> },
  options?: { props?: Record<string, string> },
): void => {
  track("pageview", {
    url: eventData?.url,
    props: {
      ...getDefaultPlausibleProps(),
      ...eventData?.props,
      ...options?.props,
    },
  });
};

// Legacy exports for backwards compatibility (no-op functions)
// These were used to enable auto-tracking which is now handled via init config
const enableAutoOutboundTracking = (): void => {
  // No-op: outboundLinks tracking is now configured via init()
  // Set outboundLinks: true in init() config to enable
};

const enableAutoPageviews = (): void => {
  // No-op: auto pageviews is now configured via init()
  // Set autoCapturePageviews: true in init() config to enable
};

export {
  enableAutoOutboundTracking,
  enableAutoPageviews,
  trackEvent,
  trackPageview,
};
